// ---------------------------------------------------------------------------
// PRNG — mulberry32. ~32-bit state, decent distribution, well-known.
// ---------------------------------------------------------------------------
/**
 * Create a seeded PRNG returning a function in [0, 1). Mulberry32 is fast and
 * good enough for grid generation — we're not doing cryptography.
 */
export function makeRand(seed) {
    let s = seed >>> 0;
    return function rand() {
        s = (s + 0x6d2b79f5) >>> 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** All directions we currently support — horizontal, vertical, diagonal (down-right). */
const ALL_DIRECTIONS = ['H', 'V', 'D'];
function step(dir) {
    switch (dir) {
        case 'H':
            return [0, 1];
        case 'V':
            return [1, 0];
        case 'D':
            return [1, 1];
    }
}
// ---------------------------------------------------------------------------
// Placement
// ---------------------------------------------------------------------------
/**
 * Build a filled grid containing every requested word, plus deterministic
 * filler letters. Returns `placements` so the renderer can highlight cells
 * on "find" without re-searching.
 *
 * If a word cannot fit after `maxAttemptsPerWord` tries, it is silently
 * dropped from `placements`. Callers should treat the returned placements as
 * the source of truth for what's actually findable in this grid — never the
 * input `words` array.
 */
export function buildGrid(opts) {
    const { words, size } = opts;
    if (!Number.isInteger(size) || size < 4) {
        throw new RangeError(`grid size must be an integer >= 4, got ${size}`);
    }
    const seed = typeof opts.seed === 'number' ? opts.seed : Math.floor(Math.random() * 2 ** 31);
    const rand = makeRand(seed);
    const maxAttempts = opts.maxAttemptsPerWord ?? 200;
    // Build an empty grid filled with a sentinel. We'll fill the gaps later.
    const EMPTY = '';
    const letters = Array.from({ length: size }, () => Array.from({ length: size }, () => EMPTY));
    // Shuffle words so placement order varies between rounds with different seeds.
    const shuffledWords = shuffle(words.map((w) => w.toUpperCase()), rand);
    const placements = [];
    for (const word of shuffledWords) {
        const placement = tryPlace(word, letters, size, rand, maxAttempts);
        if (placement)
            placements.push(placement);
    }
    // Compute the set of "must not accidentally create" target words for filler.
    // Words shorter than 3 letters are excluded — too easy to hit by accident
    // and not really "found-able" anyway. (Our bank has none, but be defensive.)
    const wordSet = new Set(shuffledWords.filter((w) => w.length >= 3));
    fillEmptyCells(letters, size, wordSet, rand);
    return { letters, placements };
}
function shuffle(arr, rand) {
    const out = arr.slice();
    for (let i = out.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rand() * (i + 1));
        const a = out[i];
        const b = out[j];
        out[i] = b;
        out[j] = a;
    }
    return out;
}
function tryPlace(word, letters, size, rand, maxAttempts) {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const dir = ALL_DIRECTIONS[Math.floor(rand() * ALL_DIRECTIONS.length)];
        const [dr, dc] = step(dir);
        // Compute the legal start range for this direction.
        const maxRow = size - 1 - (word.length - 1) * dr;
        const maxCol = size - 1 - (word.length - 1) * dc;
        if (maxRow < 0 || maxCol < 0)
            continue;
        const row = Math.floor(rand() * (maxRow + 1));
        const col = Math.floor(rand() * (maxCol + 1));
        if (canPlace(word, row, col, dr, dc, letters)) {
            writeWord(word, row, col, dr, dc, letters);
            return { word, row, col, dir };
        }
    }
    return null;
}
function canPlace(word, row, col, dr, dc, letters) {
    for (let i = 0; i < word.length; i += 1) {
        const r = row + dr * i;
        const c = col + dc * i;
        const existing = letters[r]?.[c];
        if (existing === undefined)
            return false; // off the grid
        if (existing !== '' && existing !== word[i])
            return false;
    }
    return true;
}
function writeWord(word, row, col, dr, dc, letters) {
    for (let i = 0; i < word.length; i += 1) {
        const r = row + dr * i;
        const c = col + dc * i;
        const rowArr = letters[r];
        rowArr[c] = word[i];
    }
}
// ---------------------------------------------------------------------------
// Filler
// ---------------------------------------------------------------------------
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
/**
 * Fill empty cells with random letters. We avoid (best-effort) accidentally
 * spelling a target word by re-rolling when the cell would complete one.
 *
 * This is intentionally not perfect — a fully exhaustive check would cost
 * O(size² × wordCount × wordLength × directions) per fill choice. Empirically
 * the 3-pass re-roll catches almost every conflict on an 11x11 grid with
 * 6-word target sets. Anything that slips through is "an extra path the
 * player could take" — which the renderer still accepts since `findLine`
 * compares against the placed word set, not the displayed letters.
 */
function fillEmptyCells(letters, size, wordSet, rand) {
    const MAX_REROLLS = 3;
    for (let r = 0; r < size; r += 1) {
        for (let c = 0; c < size; c += 1) {
            const rowArr = letters[r];
            if (rowArr[c] !== '')
                continue;
            let chosen = '';
            for (let attempt = 0; attempt <= MAX_REROLLS; attempt += 1) {
                const candidate = ALPHABET[Math.floor(rand() * ALPHABET.length)];
                rowArr[c] = candidate;
                if (attempt === MAX_REROLLS || !completesAnyWord(letters, size, r, c, wordSet)) {
                    chosen = candidate;
                    break;
                }
            }
            rowArr[c] = chosen;
        }
    }
}
/**
 * Does placing the current letter at (r, c) accidentally complete one of the
 * target words on any axis passing through this cell? Cheap heuristic — we
 * only look at lines fully on the grid that include (r, c).
 */
function completesAnyWord(letters, size, r, c, wordSet) {
    // For each direction, look at every line passing through (r, c) of length
    // equal to a target word's length, and check both forward and reverse.
    const lengths = new Set();
    for (const w of wordSet)
        lengths.add(w.length);
    for (const dir of ALL_DIRECTIONS) {
        const [dr, dc] = step(dir);
        for (const len of lengths) {
            // Lines of `len` passing through (r, c): start offsets in [-(len-1)..0]
            for (let offset = -(len - 1); offset <= 0; offset += 1) {
                const sr = r + dr * offset;
                const sc = c + dc * offset;
                const er = sr + dr * (len - 1);
                const ec = sc + dc * (len - 1);
                if (sr < 0 || sc < 0 || er >= size || ec >= size)
                    continue;
                let s = '';
                let complete = true;
                for (let i = 0; i < len; i += 1) {
                    const rr = sr + dr * i;
                    const cc = sc + dc * i;
                    const cell = letters[rr]?.[cc];
                    if (!cell) {
                        complete = false;
                        break;
                    }
                    s += cell;
                }
                if (!complete)
                    continue;
                if (wordSet.has(s))
                    return true;
                const reversed = s.split('').reverse().join('');
                if (wordSet.has(reversed))
                    return true;
            }
        }
    }
    return false;
}
// ---------------------------------------------------------------------------
// Selection-evaluation helper
// ---------------------------------------------------------------------------
/**
 * Given the grid and two endpoint cells, return the word that lies between
 * them (forward or reversed) if it matches one of the target words, else null.
 *
 * Accepts H, V, D-down-right, and their reverses (which are the same line
 * read the other way — we test both `s` and `s.reversed`).
 *
 * Why this lives next to the grid: the grid owns the source of truth for
 * what's "in" it. Selection evaluation reads from `placements`, not the
 * letters, so accidental words formed by filler don't count.
 */
export function findLine(letters, placements, fromRC, toRC) {
    const [fr, fc] = fromRC;
    const [tr, tc] = toRC;
    const dRow = tr - fr;
    const dCol = tc - fc;
    // Must be a straight line in one of the supported directions.
    // Horizontal: dRow == 0, dCol != 0
    // Vertical:   dRow != 0, dCol == 0
    // Diagonal:   |dRow| == |dCol|, both != 0
    const isH = dRow === 0 && dCol !== 0;
    const isV = dCol === 0 && dRow !== 0;
    const isD = Math.abs(dRow) === Math.abs(dCol) && dRow !== 0;
    if (!isH && !isV && !isD)
        return null;
    const length = Math.max(Math.abs(dRow), Math.abs(dCol)) + 1;
    const stepR = dRow === 0 ? 0 : dRow / Math.abs(dRow);
    const stepC = dCol === 0 ? 0 : dCol / Math.abs(dCol);
    const cells = [];
    let s = '';
    for (let i = 0; i < length; i += 1) {
        const r = fr + stepR * i;
        const c = fc + stepC * i;
        const ch = letters[r]?.[c];
        if (ch === undefined)
            return null;
        s += ch;
        cells.push([r, c]);
    }
    const reversed = s.split('').reverse().join('');
    // Only match against the *placed* words — the grid's own truth. Accidental
    // letter sequences formed by filler don't count, even if they happen to
    // match a target word.
    for (const p of placements) {
        if (p.word === s || p.word === reversed) {
            return { word: p.word, cells };
        }
    }
    return null;
}
/**
 * Returns true if (toR, toC) is reachable from (fromR, fromC) along one of
 * the supported axes (H, V, D), regardless of distance. Used by the renderer
 * to dim "unreachable" cells while a start cell is selected.
 */
export function isOnAxis(fromRC, toRC) {
    const [fr, fc] = fromRC;
    const [tr, tc] = toRC;
    if (fr === tr && fc === tc)
        return false; // same cell — not "reachable" in the selection sense
    const dRow = tr - fr;
    const dCol = tc - fc;
    if (dRow === 0 || dCol === 0)
        return true;
    return Math.abs(dRow) === Math.abs(dCol);
}
/** Every direction `buildRichGrid` can place a word in. Exported so callers can subset it. */
export const RICH_DIRECTIONS = [
    'H+', 'H-', 'V+', 'V-', 'D++', 'D-+', 'D+-', 'D--',
];
function richStep(dir) {
    switch (dir) {
        case 'H+': return [0, 1];
        case 'H-': return [0, -1];
        case 'V+': return [1, 0];
        case 'V-': return [-1, 0];
        case 'D++': return [1, 1];
        case 'D-+': return [-1, 1];
        case 'D+-': return [1, -1];
        case 'D--': return [-1, -1];
    }
}
/**
 * English letter frequency (Norvig 2013 corpus, rounded). Used as the
 * weighted draw for filler letters. The sum is 1.0 (rounding included).
 */
const ENGLISH_FREQ = [
    ['E', 0.1270], ['T', 0.0906], ['A', 0.0817], ['O', 0.0751], ['I', 0.0697],
    ['N', 0.0675], ['S', 0.0633], ['H', 0.0609], ['R', 0.0599], ['D', 0.0425],
    ['L', 0.0403], ['C', 0.0278], ['U', 0.0276], ['M', 0.0241], ['W', 0.0236],
    ['F', 0.0223], ['G', 0.0202], ['Y', 0.0197], ['P', 0.0193], ['B', 0.0149],
    ['V', 0.0098], ['K', 0.0077], ['J', 0.0015], ['X', 0.0015], ['Q', 0.0010],
    ['Z', 0.0007],
];
/** Cumulative-distribution lookup table for `ENGLISH_FREQ`, computed once. */
const ENGLISH_CDF = (() => {
    let acc = 0;
    return ENGLISH_FREQ.map(([letter, freq]) => {
        acc += freq;
        return [letter, acc];
    });
})();
function pickEnglishLetter(rand) {
    const x = rand();
    for (const [letter, threshold] of ENGLISH_CDF) {
        if (x <= threshold)
            return letter;
    }
    return 'E';
}
/**
 * Build a richer 8-direction grid. Words try random direction + origin up to
 * `maxAttemptsPerWord` times; if no placement fits, the word is skipped and
 * surfaced in `unplaced`. Filler letters draw from English-letter-frequency.
 *
 * Intersections (a placed cell whose letter matches a new word's letter at
 * that step) are accepted — they make the grid denser and feel more
 * crossword-like. Pure-overwrite of an existing letter is not allowed.
 */
export function buildRichGrid(opts) {
    const { words, size } = opts;
    if (!Number.isInteger(size) || size < 4) {
        throw new RangeError(`grid size must be an integer >= 4, got ${size}`);
    }
    const directions = opts.directions ?? RICH_DIRECTIONS;
    if (directions.length === 0) {
        throw new RangeError('directions must not be empty');
    }
    const seed = typeof opts.seed === 'number' ? opts.seed : Math.floor(Math.random() * 2 ** 31);
    const rand = makeRand(seed);
    const maxAttempts = opts.maxAttemptsPerWord ?? 240;
    const EMPTY = '';
    const letters = Array.from({ length: size }, () => Array.from({ length: size }, () => EMPTY));
    // Shuffle words. Then sort by length descending so the longest words —
    // which have the fewest legal placements — claim space first.
    const upper = words.map((w) => w.toUpperCase());
    const shuffled = upper.slice();
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rand() * (i + 1));
        const a = shuffled[i];
        const b = shuffled[j];
        shuffled[i] = b;
        shuffled[j] = a;
    }
    shuffled.sort((a, b) => b.length - a.length);
    // Give each word a PREFERRED direction by walking a seeded shuffle of the allowed set, so the
    // set is genuinely exercised rather than sampled independently per word.
    //
    // Independent sampling looks fair per word and is not. Measured over 400 seeds with six words on
    // a 14x14 grid and the two-direction `gentle` set, the histogram comes out even (1183 across vs
    // 1217 down) while **8% of seeds place every word in the SAME direction**. Independence alone
    // predicts ~3%; the excess is positive correlation — a grid full of horizontal runs leaves
    // horizontal gaps, so early choices pull later ones after them. A single-direction word search
    // reads as broken, and it makes a sheet that says "across and down" wrong from the other side.
    //
    // Round-robin over a shuffled cycle removes the failure mode by construction rather than reducing
    // its probability: with n words and k allowed directions, each direction is preferred ⌈n/k⌉ or
    // ⌊n/k⌋ times. It stays a PREFERENCE — a word that cannot fit its assigned direction falls back
    // to the whole set — so balance never costs a placement.
    const cycle = shuffle([...directions], rand);
    const placements = [];
    const unplaced = [];
    shuffled.forEach((word, index) => {
        const placement = tryPlaceRich(word, letters, size, rand, maxAttempts, directions, cycle[index % cycle.length]);
        if (placement)
            placements.push(placement);
        else
            unplaced.push(word);
    });
    // Filler — English letter frequency. Best-effort avoidance of forming a
    // word from the target set on any axis through the cell.
    const wordSet = new Set(shuffled.filter((w) => w.length >= 3));
    const MAX_REROLLS = 4;
    for (let r = 0; r < size; r += 1) {
        for (let c = 0; c < size; c += 1) {
            if (letters[r][c] !== EMPTY)
                continue;
            let chosen = '';
            for (let attempt = 0; attempt <= MAX_REROLLS; attempt += 1) {
                const cand = pickEnglishLetter(rand);
                letters[r][c] = cand;
                if (attempt === MAX_REROLLS || !completesAnyWord(letters, size, r, c, wordSet)) {
                    chosen = cand;
                    break;
                }
            }
            letters[r][c] = chosen;
        }
    }
    return { letters, placements, unplaced };
}
/**
 * Place one word, favouring `preferred` before considering the rest of the set.
 *
 * The preference gets the first 60% of the attempt budget. That is enough that an unobstructed
 * word essentially always lands in its assigned direction (a first attempt succeeds on a
 * near-empty grid), while still leaving a real budget for the fallback so a word boxed in by
 * earlier placements is never dropped for the sake of a tidy distribution — `unplaced` is the one
 * outcome print cannot tolerate.
 */
function tryPlaceRich(word, letters, size, rand, maxAttempts, directions = RICH_DIRECTIONS, preferred) {
    const preferredAttempts = preferred ? Math.floor(maxAttempts * 0.6) : 0;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const dir = attempt < preferredAttempts
            ? preferred
            : directions[Math.floor(rand() * directions.length)];
        const [dr, dc] = richStep(dir);
        // Compute legal origin range so the whole word fits inside [0..size-1]
        // for both axes given this direction.
        const minRow = dr >= 0 ? 0 : -dr * (word.length - 1);
        const maxRow = size - 1 - (dr > 0 ? dr * (word.length - 1) : 0);
        const minCol = dc >= 0 ? 0 : -dc * (word.length - 1);
        const maxCol = size - 1 - (dc > 0 ? dc * (word.length - 1) : 0);
        if (minRow > maxRow || minCol > maxCol)
            continue;
        const row = minRow + Math.floor(rand() * (maxRow - minRow + 1));
        const col = minCol + Math.floor(rand() * (maxCol - minCol + 1));
        // Check fit (intersections OK).
        let ok = true;
        for (let i = 0; i < word.length; i += 1) {
            const r = row + dr * i;
            const c = col + dc * i;
            const existing = letters[r][c];
            if (existing !== '' && existing !== word[i]) {
                ok = false;
                break;
            }
        }
        if (!ok)
            continue;
        // Commit.
        const cells = [];
        for (let i = 0; i < word.length; i += 1) {
            const r = row + dr * i;
            const c = col + dc * i;
            letters[r][c] = word[i];
            cells.push([r, c]);
        }
        return { word, row, col, dir, cells };
    }
    return null;
}
/**
 * Selection evaluator that uses `RichPlacement`'s pre-computed `cells` so
 * callers don't have to recompute the line from endpoints (and so we don't
 * accidentally match a longer placement that includes the selected line as
 * a substring).
 *
 * Acceptance rule: a selection from `from` to `to` matches a placement if
 * either endpoint set matches the placement's first+last cells (forward or
 * reverse). This is strict — partial selections of a longer word don't
 * count. The exact line is the placement's own `cells`.
 */
export function findRichLine(placements, fromRC, toRC) {
    for (const p of placements) {
        const head = p.cells[0];
        const tail = p.cells[p.cells.length - 1];
        const matchesForward = head[0] === fromRC[0] && head[1] === fromRC[1] &&
            tail[0] === toRC[0] && tail[1] === toRC[1];
        const matchesReverse = tail[0] === fromRC[0] && tail[1] === fromRC[1] &&
            head[0] === toRC[0] && head[1] === toRC[1];
        if (matchesForward || matchesReverse) {
            return { word: p.word, cells: p.cells };
        }
    }
    return null;
}
/** True if (from, to) lie on any straight axis — H, V, or either diagonal. */
export function isOnRichAxis(fromRC, toRC) {
    return isOnAxis(fromRC, toRC);
}
//# sourceMappingURL=grid.js.map